import { io } from 'socket.io-client';

const SOCKET_PATH = '/api/socket.io';
const NAMESPACE = '/live-chat';

class LiveChatSocketService {
    constructor() {
        this.socket = null;
        this.token = null;
        this.adminId = null;
        this.listeners = new Map();
        this.connected = false;
        this.joined = false;
    }

    connect(token, adminId) {
        if (this.socket && this.socket.connected) {
            this.token = token;
            this.adminId = adminId;
            this._joinAsAdmin();
            return this.socket;
        }

        this.token = token;
        this.adminId = adminId;

        const url = `${window.location.origin}${NAMESPACE}`;
        this.socket = io(url, {
            path: SOCKET_PATH,
            transports: ['polling', 'websocket'],
            reconnection: true,
            reconnectionAttempts: Infinity,
            reconnectionDelay: 1000,
            reconnectionDelayMax: 5000,
            withCredentials: true
        });

        this.socket.on('connect', () => {
            this.connected = true;
            console.log('[live-chat] socket connected', this.socket.id);
            this._joinAsAdmin();
        });

        this.socket.on('disconnect', (reason) => {
            this.connected = false;
            this.joined = false;
            console.log('[live-chat] socket disconnected:', reason);
        });

        this.socket.on('connect_error', (err) => {
            console.warn('[live-chat] connect_error:', err.message);
        });

        this.socket.on('reconnect', (n) => {
            console.log('[live-chat] reconnected (attempt', n + ')');
        });

        this._reattachListeners();
        return this.socket;
    }

    _joinAsAdmin() {
        if (!this.socket || !this.token || this.joined) return;
        this.socket.emit('admin:join', { token: this.token, admin_id: this.adminId }, (ack) => {
            if (ack?.ok) {
                this.joined = true;
                console.log('[live-chat] admin:join ok', ack);
            } else {
                console.error('[live-chat] admin:join failed', ack);
            }
        });
    }

    disconnect() {
        if (this.socket) {
            this.socket.disconnect();
            this.socket = null;
        }
        this.connected = false;
        this.joined = false;
    }

    on(event, callback) {
        if (!this.listeners.has(event)) this.listeners.set(event, new Set());
        this.listeners.get(event).add(callback);
        if (this.socket) this.socket.on(event, callback);
        return () => this.off(event, callback);
    }

    off(event, callback) {
        const set = this.listeners.get(event);
        if (set) set.delete(callback);
        if (this.socket) this.socket.off(event, callback);
    }

    emit(event, data, callback) {
        if (!this.socket || !this.socket.connected) {
            console.warn('[live-chat] emit before connected:', event);
            if (typeof callback === 'function') callback({ ok: false, error: 'not connected' });
            return;
        }
        this.socket.emit(event, data, callback);
    }

    _reattachListeners() {
        if (!this.socket) return;
        for (const [event, set] of this.listeners.entries()) {
            for (const cb of set) this.socket.on(event, cb);
        }
    }

    isConnected() {
        return this.connected && this.joined;
    }
}

const liveChatSocket = new LiveChatSocketService();
export default liveChatSocket;
